import { GAME_HEIGHT, GAME_WIDTH } from './config/game.config';

export const PHONE_LANDSCAPE_MAX_WIDTH = 960;
export const PHONE_LANDSCAPE_MAX_HEIGHT = 520;
export const MIN_TOUCH_TARGET_CSS_PX = 24;
export const COMFORT_TOUCH_TARGET_CSS_PX = 44;

/**
 * Below this canvas scale, the board's minimum 56-logical-px cell (see
 * campaignV2Grid.ts) renders under the 24 CSS px touch-target floor, so
 * combat itself — not just its controls — is unsupported. This is the
 * shared floor every phone-composition layout resolver (combat, menu
 * screens, prep flow) checks before attempting phone-specific sizing.
 */
export const MIN_PHONE_SCALE = MIN_TOUCH_TARGET_CSS_PX / 56;

export interface CssSize {
  width: number;
  height: number;
}

export interface SafeAreaInsets {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export type ViewportComposition = 'standard' | 'phone-landscape' | 'unsupported-phone-portrait';

export interface ResponsiveViewport {
  composition: ViewportComposition;
  viewport: CssSize;
  usableViewport: CssSize;
  canvas: CssSize;
  safeArea: SafeAreaInsets;
  canvasScale: number;
}

const ZERO_SAFE_AREA: SafeAreaInsets = { top: 0, right: 0, bottom: 0, left: 0 };

function assertFiniteNonNegative(value: number, name: string): void {
  if (!Number.isFinite(value) || value < 0) {
    throw new Error(`Invalid responsive viewport ${name}: expected a finite non-negative number`);
  }
}

export function resolveResponsiveViewport(
  viewport: CssSize,
  canvas: CssSize,
  safeArea: SafeAreaInsets = ZERO_SAFE_AREA,
): ResponsiveViewport {
  assertFiniteNonNegative(viewport.width, 'width');
  assertFiniteNonNegative(viewport.height, 'height');
  assertFiniteNonNegative(canvas.width, 'canvas width');
  assertFiniteNonNegative(canvas.height, 'canvas height');
  for (const [edge, value] of Object.entries(safeArea)) {
    assertFiniteNonNegative(value, `safe-area ${edge}`);
  }

  const usableViewport = {
    width: viewport.width - safeArea.left - safeArea.right,
    height: viewport.height - safeArea.top - safeArea.bottom,
  };
  if (usableViewport.width <= 0 || usableViewport.height <= 0) {
    throw new Error('Invalid responsive viewport safe area: insets consume the viewport');
  }

  const phoneSized = Math.max(viewport.width, viewport.height) <= PHONE_LANDSCAPE_MAX_WIDTH
    && Math.min(viewport.width, viewport.height) <= PHONE_LANDSCAPE_MAX_HEIGHT;
  const composition: ViewportComposition = phoneSized
    ? viewport.width > viewport.height ? 'phone-landscape' : 'unsupported-phone-portrait'
    : 'standard';

  const canvasScale = canvas.width > 0 && canvas.height > 0
    ? Math.min(canvas.width / GAME_WIDTH, canvas.height / GAME_HEIGHT)
    : 0;

  return { composition, viewport, usableViewport, canvas, safeArea, canvasScale };
}

/** Whether a composition supports phone-specific layout resolution at all (shared gate for combat, menu, and prep-flow resolvers). */
export function isPhoneCompositionSupported(responsive: ResponsiveViewport): boolean {
  return responsive.composition === 'phone-landscape' && responsive.canvasScale >= MIN_PHONE_SCALE;
}

export function logicalPixelsForCssTarget(
  responsive: ResponsiveViewport,
  targetCssPx: number,
): number {
  assertFiniteNonNegative(targetCssPx, 'target size');
  if (responsive.canvasScale <= 0) {
    throw new Error('Cannot resolve a logical touch target from a collapsed canvas');
  }
  return Math.ceil(targetCssPx / responsive.canvasScale);
}

function readCssPixel(style: CSSStyleDeclaration, property: string): number {
  const parsed = Number.parseFloat(style.getPropertyValue(property));
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
}

export function readSafeAreaInsets(doc: Document = document): SafeAreaInsets {
  const style = doc.defaultView?.getComputedStyle(doc.documentElement);
  if (!style) return ZERO_SAFE_AREA;
  return {
    top: readCssPixel(style, '--safe-area-top'),
    right: readCssPixel(style, '--safe-area-right'),
    bottom: readCssPixel(style, '--safe-area-bottom'),
    left: readCssPixel(style, '--safe-area-left'),
  };
}

export function readResponsiveViewport(
  canvas: HTMLCanvasElement,
  view: Window = window,
  doc: Document = document,
): ResponsiveViewport {
  const bounds = canvas.getBoundingClientRect();
  const visualViewport = view.visualViewport;
  return resolveResponsiveViewport(
    {
      width: visualViewport?.width ?? view.innerWidth,
      height: visualViewport?.height ?? view.innerHeight,
    },
    { width: bounds.width, height: bounds.height },
    readSafeAreaInsets(doc),
  );
}
